import React, { useState, useRef, useEffect } from 'react'
import { sendEmail } from './api.js'
import { isServer } from './isServer.js'

const RentalAppraisalForm = () => {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [propertyAddress, setPropertyAddress] = useState('')
  const [emailInvalid, setEmailInvalid] = useState(false)
  const [kellandsEmail, setKellandsEmail] = useState(false)
  const [nameInvalid, setNameInvalid] = useState(false)
  const [phoneInvalid, setPhoneInvalid] = useState(false)
  const [addressInvalid, setAddressInvalid] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [rateLimited, setRateLimited] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [honeypot, setHoneypot] = useState('')
  const mountTime = useRef(Date.now())
  const autocompleteRef = useRef(null)

  useEffect(() => {
    if (!isServer && window.google) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          document.getElementById('ra_address'),
          { types: ['address'], componentRestrictions: { country: 'nz' } }
        )
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace()
          if (place && place.formatted_address) {
            setPropertyAddress(place.formatted_address)
            setAddressInvalid(false)
          }
        })
      } catch (e) {
        // silent fail
      }

      const input = document.getElementById('ra_address')
      if (input) {
        const observer = new MutationObserver(() => {
          if (input.disabled) {
            input.disabled = false
            input.placeholder = ''
            input.style.backgroundImage = 'none'
            observer.disconnect()
          }
        })
        observer.observe(input, { attributes: true, attributeFilter: ['disabled', 'placeholder'] })
      }
    }

    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [])

  const validateEmail = (email) => {
    // eslint-disable-next-line
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email)
  }

  const validateName = (name) => {
    return name.trim().length >= 2 && name.length <= 100 && !/[<>{}\/\\]/.test(name)
  }

  const validatePhone = (phone) => {
    return !phone.length || /^[0-9\s\+\-\(\)]{6,20}$/.test(phone)
  }

  const validateAddress = (address) => {
    return address.trim().length >= 5 && !/[<>{}]/.test(address)
  }

  const resetForm = () => {
    setName('')
    setPhone('')
    setEmail('')
    setPropertyAddress('')
    setEmailInvalid(false)
    setKellandsEmail(false)
    setNameInvalid(false)
    setPhoneInvalid(false)
    setAddressInvalid(false)
    setError(false)
    setRateLimited(false)
    setSubmitting(false)
  }

  const handleError = (err) => {
    if (err && err.status === 429) {
      setRateLimited(true)
    } else {
      setError(true)
    }
    setSubmitting(false)
  }

  const submitForm = () => {
    if (submitting) return
    if (honeypot.length) return
    if (Date.now() - mountTime.current < 3000) return

    // Reset all errors
    setEmailInvalid(false)
    setKellandsEmail(false)
    setNameInvalid(false)
    setPhoneInvalid(false)
    setAddressInvalid(false)
    setError(false)
    setRateLimited(false)

    // Validate all fields, collect errors rather than returning on first failure
    let hasErrors = false

    if (!validateName(name)) {
      setNameInvalid(true)
      hasErrors = true
    }

    if (email.toLowerCase().includes('@kellands.co.nz')) {
      setKellandsEmail(true)
      hasErrors = true
    } else if (!validateEmail(email)) {
      setEmailInvalid(true)
      hasErrors = true
    }

    if (!validatePhone(phone)) {
      setPhoneInvalid(true)
      hasErrors = true
    }

    if (!validateAddress(propertyAddress)) {
      setAddressInvalid(true)
      hasErrors = true
    }

    if (hasErrors) return

    setSubmitting(true)

    sendEmail(
      {
        name: name.trim(),
        phone: phone.trim(),
        email,
        propertyAddress: propertyAddress.trim()
      },
      'rental_appraisal',
      () => setSuccess(true),
      handleError
    )
  }

  if (success) {
    return (
      <div className="rentals-text-row">
        <p className="rentals-p">Thanks for your enquiry. We will be in touch.</p>
      </div>
    )
  }

  return (
    <div className="rentals-text-row">
      <div className="rentals-sub">
        <h1>Book Your Free Rental Health Check</h1>
        <p className="rentals-p">Complete the form below and one of our team members will be in touch with you as soon as possible.</p>
      </div>
      <div>
        <fieldset>
          {/* Honeypot - hidden from real users, visible to bots */}
          <p style={{ display: 'none' }}>
            <label htmlFor="f_website">Website</label>
            <input
              type="text"
              id="f_website"
              name="f_website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex="-1"
              autoComplete="off"
            />
          </p>
          <p className="contactus-inputs">
            <label className="required" htmlFor="ra_name">Full Name *</label>
            <input
              className={`contactus-input contactus-input-${nameInvalid}`}
              type="text"
              id="ra_name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameInvalid(false)
              }}
            />
          </p>
          {nameInvalid && <p style={{ color: '#df1212', fontSize: '14px', margin: '0 0 8px' }}>Please enter a valid name.</p>}
          <p className="contactus-inputs">
            <label htmlFor="ra_phone">Phone Number</label>
            <input
              className={`contactus-input contactus-input-${phoneInvalid}`}
              type="text"
              id="ra_phone"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                setPhoneInvalid(false)
              }}
            />
          </p>
          {phoneInvalid && <p style={{ color: '#df1212', fontSize: '14px', margin: '0 0 8px' }}>Please enter a valid phone number.</p>}
          <p className="contactus-inputs">
            <label className="required" htmlFor="ra_email">Email Address *</label>
            <input
              className={`contactus-input contactus-input-${emailInvalid || kellandsEmail}`}
              type="text"
              id="ra_email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailInvalid(false)
                setKellandsEmail(false)
              }}
            />
          </p>
          {emailInvalid && <p style={{ color: '#df1212', fontSize: '14px', margin: '0 0 8px' }}>Please enter a valid email address.</p>}
          {kellandsEmail && <p style={{ color: '#df1212', fontSize: '14px', margin: '0 0 8px' }}>Please use a non-Kellands email address.</p>}
          <p className="contactus-inputs">
            <label className="required" htmlFor="ra_address">Address of Rental Property *</label>
            <input
              className={`contactus-input contactus-input-${addressInvalid}`}
              type="text"
              id="ra_address"
              value={propertyAddress}
              onChange={(e) => {
                setPropertyAddress(e.target.value)
                setAddressInvalid(false)
              }}
            />
          </p>
          {addressInvalid && <p style={{ color: '#df1212', fontSize: '14px', margin: '0 0 8px' }}>Please enter a valid property address.</p>}
        </fieldset>
        <fieldset className="contactus-submit-container">
          <p><em>Note: fields marked with a <span>*</span> are required to submit this form.</em></p>
          {rateLimited && <p style={{ color: '#df1212', fontSize: '14px' }}>Too many submissions. Please wait a moment before trying again.</p>}
          {error && <p style={{ color: '#df1212', fontSize: '14px' }}>Something went wrong. Please try again.</p>}
          <div className="contactus-submit-btns">
            <input
              type="reset"
              className="contactus-form-btn contactus-reset"
              value="Reset"
              onClick={resetForm}
            />
            <input
              type="submit"
              className="contactus-form-btn"
              value={submitting ? 'Sending...' : 'Submit'}
              onClick={submitForm}
              disabled={submitting}
            />
          </div>
        </fieldset>
        <p className="rentals-p" style={{ marginTop: '16px' }}>
          Most landlords contact us because they feel like just another number, communication has dropped off,
          they're unsure if their rent is maximised or feel that maintenance isn't being handled proactively
          causing tenants to leave.
        </p>
        <p className="rentals-p" style={{ marginTop: '12px' }}>
          If this is you, we look forward to hearing from you — you might be losing money without realising it.
        </p>
      </div>
    </div>
  )
}

export default RentalAppraisalForm